import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import { UserAccessConstants } from './userAccess';

export {};

export const addPrefix = (prefix: string, value: string | number) => {
  return prefix + value;
};

export const addSuffix = (value: string | number, suffix: string) => {
  return value + suffix;
};

export const numberFormatter = (num: number, denominator: number) => {
  return Math.floor(num / denominator) - num / denominator
    ? (num / denominator).toFixed(2)
    : num / denominator;
};

export const numFormatterWithSuffix = (
  num: number,
  roundOfDecimal?: number,
) => {
  if (num < 1000) {
    return num.toFixed(roundOfDecimal!).toString();
  } else if (num >= 1000 && num < 1000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000)).toFixed(roundOfDecimal) + 'K'
      : numberFormatter(num, 1000) + 'K';
  } else if (num >= 1000000 && num < 1000000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000000)).toFixed(roundOfDecimal) + 'M'
      : numberFormatter(num, 1000000) + 'M';
  } else if (num >= 1000000000 && num < 1000000000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000000000)).toFixed(roundOfDecimal) + 'B'
      : numberFormatter(num, 1000000000) + 'B';
  } else if (num >= 1000000000000 && num < 1000000000000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000000000000)).toFixed(roundOfDecimal) +
          'T'
      : numberFormatter(num, 1000000000000) + 'T';
  } else {
    return num + '';
  }
};

export const numFormatterWithSuffixForMillion = (
  num: number,
  roundOfDecimal?: number,
) => {
  if (num < 1000000) {
    return new Intl.NumberFormat('en-US', {
      currency: 'USD',
      minimumFractionDigits: roundOfDecimal,
      maximumFractionDigits: roundOfDecimal,
    })
      .format(num)
      .toString();
  } else if (num >= 1000000 && num < 1000000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000000)).toFixed(roundOfDecimal) + 'M'
      : numberFormatter(num, 1000000) + 'M';
  } else if (num >= 1000000000 && num < 1000000000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000000000)).toFixed(roundOfDecimal) + 'B'
      : numberFormatter(num, 1000000000) + 'B';
  } else if (num >= 1000000000000 && num < 1000000000000000) {
    return roundOfDecimal
      ? Number(numberFormatter(num, 1000000000000)).toFixed(roundOfDecimal) +
          'T'
      : numberFormatter(num, 1000000000000) + 'T';
  } else {
    return num + '';
  }
};

export const numFormatterWithoutSuffix = (num: number) => {
  const str = numFormatterWithSuffix(num);
  return str.substring(0, str.length - 1);
};

export const numFormatterWithOnlySuffix = (num: number) => {
  const str = numFormatterWithSuffix(num);
  return str[str.length - 1];
};

export const roundNumber = (number: number, decimalDigit: number) => {
  const powerOften = Math.pow(10, decimalDigit);
  const result = Math.round(number * powerOften) / powerOften;
  return result;
};

export const formatDate = (
  dateString: string,
  dateFormat: string,
  inputFormat?: string,
) => {
  const date = moment(dateString, inputFormat).format(dateFormat);
  return date;
};

export const toISOStringFormat = (formattedDate: string) => {
  const date = moment(formattedDate, 'MMMM Do YYYY, h:mm A').format(
    'YYYY-MM-DDTHH:mm',
  );
  return date;
};

export const formatDateToOrdinal = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();

  const getOrdinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return `${getOrdinal(day)} ${month} ${year}`;
};

export const capitalizeString = (
  stringToBeCapitalized: string,
  splitBy?: string,
  joinBy?: string,
) => {
  if (stringToBeCapitalized === '' || null === stringToBeCapitalized) {
    return '';
  }
  let wordsArray: any[] = [stringToBeCapitalized];
  if (stringToBeCapitalized) {
    wordsArray = stringToBeCapitalized.split(splitBy ?? ' ');
  }
  for (let i = 0; i < wordsArray.length && wordsArray[i]; i++) {
    wordsArray[i] =
      wordsArray[i].charAt(0).toUpperCase() +
      wordsArray[i].slice(1).toLowerCase();
  }
  return wordsArray.join(joinBy ?? ' ');
};

export const numberToCurrencyFormatter = (
  value: number,
  showDollarSign: boolean = true,
  fractionDigits: number = 2,
) => {
  const currencySymbol = showDollarSign ? (value >= 0 ? '$' : '-$') : '';
  const number = Math.abs(value);
  return (
    currencySymbol +
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(number)
  );
};

export const convertDigitDateToReadable = (dateString: string) => {
  const [month, day, year] = dateString.split('-');
  const date = new Date(`${year}-${month}-${day}`);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const getMonthInNumberFormat = (value: string) => {
  switch (value) {
    case 'Jan':
      return '01';
    case 'Feb':
      return '02';
    case 'Mar':
      return '03';
    case 'Apr':
      return '04';
    case 'May':
      return '05';
    case 'Jun':
      return '06';
    case 'Jul':
      return '07';
    case 'Aug':
      return '08';
    case 'Sep':
      return '09';
    case 'Oct':
      return '10';
    case 'Nov':
      return '11';
    case 'Dec':
      return '12';

    default:
      return 'Invalid Month';
  }
};

export const getMonthInResponseFormat = (value: string) => {
  switch (value) {
    case '01':
    case '1':
      return 'Jan';
    case '02':
    case '2':
      return 'Feb';
    case '03':
    case '3':
      return 'Mar';
    case '04':
    case '4':
      return 'Apr';
    case '05':
    case '5':
      return 'May';
    case '06':
    case '6':
      return 'Jun';
    case '07':
    case '7':
      return 'Jul';
    case '08':
    case '8':
      return 'Aug';
    case '09':
    case '9':
      return 'Sep';
    case '10':
      return 'Oct';
    case '11':
      return 'Nov';
    case '12':
      return 'Dec';

    default:
      return 'Invalid Month';
  }
};

export const commasForNumbers = (num: string, decimals?: number) => {
  return Number(parseFloat(num).toFixed(decimals === 0 ? 0 : 2)).toLocaleString(
    'en-US',
    {
      minimumFractionDigits: decimals,
    },
  );
};

export const getLatestDate = (datesArray: string[]) => {
  const moments = datesArray.map((date) => moment(date));
  const maxDate = moment.max(moments);
  return maxDate.format('YYYY-MM-DD');
};

export const getPreviousYearDates = (startDate: string, endDate: string) => {
  const prevStartDate = moment(startDate).subtract(1, 'year').toString();
  const prevEndDate = moment(endDate).subtract(1, 'year').toString();

  return { prevStartDate, prevEndDate };
};

export const getMonths = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const months = [];
  while (startDate <= endDate) {
    months.push({
      label: startDate.toLocaleString('default', { month: 'short' }),
    });
    startDate.setMonth(startDate.getMonth() + 1);
  }
  return months;
};

export const getDateFromMonthAndYear = (
  month: string,
  year: number,
  isEndDate?: boolean,
) => {
  const date = moment(`${month} ${year}`, 'MMM YYYY');
  const dateString = isEndDate
    ? date.endOf('month').format('YYYY-MM-DD')
    : date.startOf('month').format('YYYY-MM-DD');
  return dateString;
};

export const arrayToString = (data: string[]) => {
  let str = '';
  data?.map((item, index) => {
    str += item + (data?.length - 1 !== index ? ' | ' : '');
  });
  return str;
};

export const wrongValuesNeglected = [
  '*',
  'J',
  'na',
  'NA',
  'Na',
  'nA',
  'NAN',
  'nan',
  'Nan',
  'nat',
  'Nat',
  'NAT',
  'None',
  'none',
  'null',
  'NULL',
  'Null',
  '',
  null,
  undefined,
];

export const emailEncoder = (email: string) => {
  const symbols: any = {
    '@': '%40',
    '&amp;': '%26',
    '*': '%2A',
    '+': '%2B',
    '/': '%2F',
    '&lt;': '%3C',
    '&gt;': '%3E',
    '=': '%3D',
  };

  return email.replace(/([@*+/=]|&(amp|lt|gt);)/g, function (m) {
    return symbols[m];
  });
};

export const getToolText = (value: number) => {
  return `<div style="background-color:#33475B;padding: 8px;"><div style="color:#FFFFFF;font-size:13px;font-weight:500;" ><span>${numberToCurrencyFormatter(
    value,
    true,
  )}</span></div>`;
};

export const formatProivderType = (value: string) => {
  if (value === 'F') {
    return 'Facility';
  } else if (value === 'P') {
    return 'Physician';
  } else {
    return '-';
  }
};
export const formatNetworkType = (value: string) => {
  if (value === 'IN') {
    return 'In Network';
  } else {
    return 'Out Network';
  }
};

export const FileSizeFormatter = (value: number, decimals: number = 2) => {
  if (!+value) {
    return '0 Bytes';
  }

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(value) / Math.log(k));

  return `${parseFloat((value / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const isValidURL = (url: string) => {
  if (
    /^(https?:\/\/)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/g.test(
      url,
    )
  ) {
    return true;
  } else {
    return false;
  }
};

export const convertDateRange = (dateRangeStr: string) => {
  if (!dateRangeStr) {
    return '-';
  }

  const [startDateStr, endDateStr] = dateRangeStr.split(' to ');

  const startDate = moment(startDateStr, 'MM/DD/YYYY');
  const endDate = moment(endDateStr, 'MM/DD/YYYY');

  const startFormatted = startDate.format('MMM YYYY');
  const endFormatted = endDate.format('MMM YYYY');

  const result = `${startFormatted} - ${endFormatted}`;

  return result;
};

export const getGender = (gender: string) => {
  return gender === 'M' ? 'Male' : 'Female';
};

export const getCohortConditionsByType = (
  types: string[],
  cohortValues: any,
) => {
  const conditions: any[] = [];
  for (const type of types) {
    switch (type) {
      case 'chronic_conditions':
        conditions.push({
          storeName: 'chronic_conditions',
          inputType: 'multiple',
          propertyDropDownOptions: [
            'Diabetes',
            'Chronic Heart Failure',
            'Hypertension',
            'Hyperlipidemia',
            'Chronic Obstructive Pulmonary Disease (COPD)',
          ],
          categoryDropDownOptions: ['Chronic Disease'],
          conditionDropDownOptions: ['is'],
          propertyValue: cohortValues['chronic_conditions'] ?? [],
        });
        break;
      case 'high_cost_claimants':
        conditions.push({
          storeName: 'high_cost_claimants',
          inputType: 'number',
          categoryDropDownOptions: ['High Cost Claimants'],
          conditionDropDownOptions: ['is greater than equal to'],
          propertyValue: cohortValues['high_cost_claimants'] ?? '',
        });
        break;
      case 'multiple_drugs':
        conditions.push({
          storeName: 'multiple_drugs',
          inputType: 'number',
          categoryDropDownOptions: ['Drug Usage Count'],
          conditionDropDownOptions: ['is greater than equal to'],
          propertyValue: cohortValues['multiple_drugs'] ?? '',
        });
        break;
      default:
        break;
    }
  }

  return conditions;
};

export const getRatingColor = (rating: number) => {
  if (rating <= 1) {
    return '#D46A6D';
  } else if (rating <= 2) {
    return '#F2763D';
  } else if (rating <= 3) {
    return '#E1A948';
  } else if (rating <= 4) {
    return '#08C7B4';
  } else if (rating <= 5) {
    return '#15948B';
  }
  return 'grey';
};

export const getRatingText = (rating: number) => {
  if (rating <= 1) {
    return 'Poor';
  } else if (rating <= 2) {
    return 'Fair';
  } else if (rating <= 3) {
    return 'Good';
  } else if (rating <= 4) {
    return 'Very Good';
  } else if (rating <= 5) {
    return 'Excellent';
  }
  return '-';
};

export const getPriorityButton = (priority: string) => {
  switch (priority) {
    case 'High':
      return {
        labelColor: patientAccessTheme.palette.AIQChips?.textColor1,
        labelBackgroundColor: patientAccessTheme.palette.AIQChips?.background1,
      };
    case 'Medium':
      return {
        labelColor: patientAccessTheme.palette.AIQChips?.textColor2,
        labelBackgroundColor: patientAccessTheme.palette.AIQChips?.background2,
      };
    case 'Low':
      return {
        labelColor: patientAccessTheme.palette.primary[500],
        labelBackgroundColor: patientAccessTheme.palette.AIQChips?.background15,
      };
  }
  return {
    labelColor: 'grey',
    labelBackgroundColor: 'grey',
  };
};

export const getCurrentTimestamp = () => {
  return Date.now();
};

export const decryptData = (data: any) => {
  const keyBase64 = process.env.DATA_SECRET_KEY;

  if (!keyBase64 || !data) {
     
    console.error('Encryption key is missing or no data!');
    return '-';
  }

  const key = CryptoJS.enc.Base64.parse(keyBase64);

  const decryptedBytes = CryptoJS.AES.decrypt(data, key, {
    mode: CryptoJS.mode.ECB,
  });

  const decryptedData = decryptedBytes.toString(CryptoJS.enc.Utf8);
  return decryptedData;
};

export const isCareNavigator = (role: string) => {
  if (
    role === UserAccessConstants.NURSE ||
    role === UserAccessConstants.PHARMACIST ||
    role === UserAccessConstants.CUSTOMER_SERVICE
  ) {
    return true;
  }
  return false;
};

export const getCareNavigatorLabel = (role: string) => {
  switch (role) {
    case UserAccessConstants.NURSE:
      return 'Nurse';
    case UserAccessConstants.PHARMACIST:
      return 'Pharmacist';
    case UserAccessConstants.CUSTOMER_SERVICE:
      return 'Customer Service';
  }
  return '-';
};

export function formatCurrency(resultValue: number): string {
  const isNegative = resultValue < 0;
  const absValue = Math.abs(resultValue);

  let formatted: string;

  if (absValue < 10_000) {
    formatted = absValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
  } else if (absValue < 1_000_000) {
    const divided = absValue / 1_000;
    if (divided >= 100) {
      formatted = `${divided.toFixed(0)}k`;
    } else if (divided >= 10) {
      formatted = `${divided.toFixed(2)}k`;
    } else {
      formatted = `${divided.toFixed(2)}k`;
    }
  } else if (absValue < 1_000_000_000) {
    const divided = absValue / 1_000_000;
    if (divided >= 100) {
      formatted = `${divided.toFixed(0)}M`;
    } else if (divided >= 10) {
      formatted = `${divided.toFixed(2)}M`;
    } else {
      formatted = `${divided.toFixed(2)}M`;
    }
  } else if (absValue < 1_000_000_000_000) {
    const divided = absValue / 1_000_000_000;
    if (divided >= 100) {
      formatted = `${divided.toFixed(0)}B`;
    } else if (divided >= 10) {
      formatted = `${divided.toFixed(2)}B`;
    } else {
      formatted = `${divided.toFixed(2)}B`;
    }
  } else {
    const divided = absValue / 1_000_000_000_000;
    if (divided >= 100) {
      formatted = `${divided.toFixed(0)}T`;
    } else if (divided >= 10) {
      formatted = `${divided.toFixed(2)}T`;
    } else {
      formatted = `${divided.toFixed(2)}T`;
    }
  }

  return isNegative ? `$-${formatted}` : `$${formatted}`;
}

export const formatDateToDDMMMMYYYY = (dateInput: string | Date): string => {
  // Handle null, undefined, empty string
  if (!dateInput) {
    return '-';
  }

  const date = new Date(dateInput);

  // Handle invalid date object
  if (isNaN(date.getTime())) {
    return '-';
  }

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  return date.toLocaleDateString('en-GB', options);
};

export const getShortMonth = (month: string) => {
  const map: Record<string, string> = {
    January: 'Jan',
    February: 'Feb',
    March: 'Mar',
    April: 'Apr',
    May: 'May',
    June: 'Jun',
    July: 'Jul',
    August: 'Aug',
    September: 'Sep',
    October: 'Oct',
    November: 'Nov',
    December: 'Dec',
  };

  return map[month] || month;
};

export const typeOfService = [
  'Procedure (Cardiovascular)',
  'DME (Orthotic Devices)',
  'Imaging (Nuclear)',
  'Procedure (Other Organ Systems)',
  'Test (General Laboratory)',
  'Treatment (Injections and Infusions (nononcologic))',
  'Procedure (Digestive/Gastrointestinal)',
  'Procedure (Musculoskeletal)',
  'Test (Test - Miscellaneous)',
  'Test (Molecular Testing)',
  'DME (Hospital Beds)',
  'DME (Wheelchairs)',
  'Imaging (Standard X-ray)',
  'Test (Anatomic Pathology)',
  'Treatment (Treatment - Miscellaneous)',
  'Anesthesia (Anesthesia)',
  'Treatment (Chemotherapy)',
  'DME (Medical/Surgical Supplies)',
  'Imaging (Ultrasound)',
  'Imaging (Magnetic Resonance)',
  'Procedure (Eye)',
  'E&M (Behavioral Health Services)',
  'Imaging (CT Scan)',
  'Other (Vision, Hearing, and Speech Services)',
  'E&M (Critical Care Services)',
  'DME (Other DME)',
  'Procedure (Hematology)',
  'Test (Cardiography)',
  'Procedure (Breast)',
  'Procedure (Vascular)',
  'DME (Drugs Administered Through DME)',
  'Treatment (Physical, Occupational, and Speech Therapy)',
  'Procedure (Skin)',
  'Treatment (Radiation Oncology)',
  'Treatment (Dialysis)',
  'Other (Enteral and Parenteral)',
  'E&M (Care Management/Coordination)',
  'E&M (Home Services)',
  'Test (Neurologic)',
  'E&M (Hospice)',
  'Treatment (Spinal Manipulation)',
  'E&M (Office/Outpatient Services)',
  'E&M (E&M - Miscellaneous)',
  'Test (Pulmonary)',
  'E&M (Hospital Inpatient Services)',
  'E&M (Observation Care Services)',
  'DME (Oxygen and Supplies)',
  'E&M (Nursing Facility Services)',
  'E&M (Emergency Department Services)',
  'E&M (Ophthalmological Services)',
  'Imaging (Imaging - Miscellaneous)',
  'Other (Ambulance)',
];

export const placeOfService = [
  'Pharmacy ',
  'Telehealth',
  'School',
  'Homeless Shelter',
  'Indian Health Service Free-standing Facility',
  'Indian Health Service Provider-based Facility',
  'Tribal 638 Free-standing Facility',
  'Tribal 638Provider-based Facility',
  'Prison/Correctional Facility',
  'Unassigned',
  'Office',
  'Home',
  'Assisted Living Facility',
  'Group Home ',
  'Mobile Unit',
  'Temporary Lodging',
  'Walk-in Retail Health Clinic',
  'Place of Employment-',
  'Off Campus-Outpatient Hospital',
  'Urgent Care Facility',
  'Inpatient Hospital',
  'On Campus-Outpatient Hospital',
  'Emergency Room  Hospital',
  'Ambulatory Surgical Center',
  'Birthing Center',
  'Military Treatment Facility',
  'Skilled Nursing Facility',
  'Nursing Facility',
  'Custodial Care Facility',
  'Hospice',
  'Ambulance - Land',
  'Ambulance - Air or Water',
  'Independent Clinic',
  'Federally Qualified Health Center',
  'Inpatient Psychiatric Facility',
  'Psychiatric Facility-Partial Hospitalization',
  'Community Mental Health Center',
  'Intermediate Care Facility/ Individuals with Intellectual Disabilities',
  'Residential Substance Abuse Treatment Facility',
  'Psychiatric Residential Treatment Center',
  'Non-residential Substance Abuse Treatment Facility',
  'Non-residential Opioid Treatment Facility',
  'Mass Immunization Center',
  'Comprehensive Inpatient Rehabilitation Facility',
  'Comprehensive Outpatient Rehabilitation Facility',
  'End-Stage Renal Disease Treatment Facility',
  'Public Health Clinic',
  'Rural Health Clinic',
  'Independent Laboratory',
  'Other Place of Service',
];

export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);

  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};
